import FormCurrencyInputFancy from '@c/forms/controls/FormCurrencyInputFancy';
import FormSelect from '@c/forms/controls/FormSelect';
import AddAddressModal from '@c/modals/AddAddressModal';
import AddPaymentMethodModal from '@c/modals/AddPaymentMethodModal';
import { BProcess, ProcessStep } from '@c/process/BaseProcess';
import { CreditCardIcon, PencilIcon } from '@heroicons/react/20/solid';
import { useMutation, useQuery } from '@tanstack/react-query';
import Button from '@ui/Button';
import Checkbox from '@ui/Checkbox';
import SafeImage from '@ui/SafeImage';
import { calculateShipping, getShippingRegion } from '@util/firestore/cart';
import { getPaymentMethods } from '@util/firestore/payments';
import { calcTax, formatCurrency } from '@util/index';
import { capitalize } from 'lodash';
import { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { usePlaceABid } from '../hooks/usePlaceABid';
import { ProductDocument } from 'models/product';
import useRealTimeBids from '@util/hooks/auctions/useRealtimeBids';
import { useAuth } from 'context/AuthContext';
import { PlaceABidStep } from '../types';
import Link from 'next/link';

export const PlaceABidForm = ({
  product,
  onSubmitSuccess,
}: {
  product: ProductDocument;
  onSubmitSuccess: () => void;
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [openModal, setOpenModal] = useState<string | null>(null);
  const { data: paymentMethodData } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: () => getPaymentMethods(),
  });
  const paymentOptions = paymentMethodData?.map((paymentMethod: any) => ({
    id: paymentMethod.id ?? '',
    label: `${capitalize(paymentMethod.brand)} - ${paymentMethod.last4}`,
    value: paymentMethod.id ?? '',
    selectedDisplay: (
      <div className="flex items-center gap-2">
        {['visa', 'mastercard', 'amex', 'discover'].includes(
          paymentMethod.brand?.toLowerCase() ?? ''
        ) ? (
          <SafeImage
            src={`/cc_brands/${paymentMethod.brand?.toLowerCase()}.png`}
            alt={paymentMethod.brand ?? ''}
            width={32}
            height={20}
            className="mr-2 h-12 w-16 object-contain"
          />
        ) : (
          <CreditCardIcon className="h-12 w-12" />
        )}

        <span>{`Card ending in ${paymentMethod.last4}`}</span>
      </div>
    ),
  }));
  const { userDoc } = useAuth();
  const { highestBid } = useRealTimeBids(product.id, userDoc?.uid);

  const addressOptions =
    userDoc?.addresses?.map((address) => ({
      id: `${address.address_line1}`,
      // using this validation to avoid showing empty values
      label: [
        address.address_line1,
        address.city_locality,
        address.state_province,
        address.postal_code,
      ]
        .filter(Boolean)
        .join(', '),
      value: JSON.stringify(address),
    })) ?? [];
  const {
    useFormReturn: {
      setValue,
      setError,
      watch,
      formState: { errors, isSubmitting },
      control,
      clearErrors,
      handleSubmit,
    },
    onSubmit,
  } = usePlaceABid({ product, onSubmitSuccess });
  const form = watch();

  useEffect(() => {
    if (userDoc?.addresses?.length && !form.shippingAddress) {
      const defaultAddress =
        userDoc.addresses.find((a) => a.is_default) || userDoc.addresses[0];
      if (defaultAddress) {
        setValue('shippingAddress', JSON.stringify(defaultAddress));
        const shipping = calculateShipping(
          product,
          getShippingRegion(defaultAddress)
        );
        if (shipping) {
          setValue('shippingCost', shipping.cost ?? 0);
        }
      }
    }
  }, [userDoc?.addresses, form.shippingAddress, product, setValue]);

  useEffect(() => {
    if (paymentOptions?.length && !form.paymentMethodId) {
      setValue('paymentMethodId', paymentOptions[0].value);
    }
  }, [paymentOptions, form.paymentMethodId, setValue]);
  const taxMutation = useMutation({
    mutationFn: (form: {
      price: number;
      shippingCost: number;
      shippingAddress: string;
    }) =>
      calcTax({
        form,
        products: [product],
      }),
    onSuccess: (data) => {
      setValue('tax', data);
    },
  });
  const steps: ProcessStep<PlaceABidStep>[] = [
    {
      id: 'bid-price',
      content: (
        <div className="flex h-full w-full flex-col justify-between overflow-x-hidden p-8">
          <div>
            <span className="w-[55rem] text-[1.6rem] text-brand-dark-gray">
              If you have the highest bid once the auction expires, you will be
              automatically charged your bid price.{' '}
              <Link
                className="text-brand-secondary"
                href="/faq"
                target="_blank"
              >
                Learn more
              </Link>
            </span>

            {/* hiding the highest bid tag in case there are no bids */}
            <div className="mt-8 flex items-center justify-between gap-x-2 rounded-[2rem] bg-[#E9F4E9] p-6">
              <div className="text-[1.6rem] font-light text-black">
                Current Highest Bid
              </div>

              <div className="text-h3 font-medium text-green-700">
                {formatCurrency(highestBid?.bid_price ?? product.price)}
              </div>
            </div>

            <div className="mt-8">
              <div className="flex w-full items-center justify-between rounded-[1.5rem] border border-[#D9D9D9] p-4 px-8">
                <span className="text-[2rem] font-medium text-black">
                  Your Bid
                </span>
                <Controller
                  control={control}
                  name={`price`}
                  render={({ field: { value, onChange, onBlur } }) => (
                    <FormCurrencyInputFancy
                      placeholder={'0.00'}
                      error={!!errors.price}
                      onChange={(e) => {
                        clearErrors('price');
                        onChange(e);
                      }}
                      value={value > 0 ? value.toFixed(2) : ''}
                      onBlur={onBlur}
                      className={`!text-[1.8rem] ${
                        errors.price ? 'text-brand-secondary' : '!text-black'
                      }`}
                    />
                  )}
                />
              </div>
              {errors.price && (
                <span className="text-[1.5rem] font-semibold text-brand-red ">
                  {errors.price.message}
                </span>
              )}
            </div>
            <div className="mt-8">
              <div className="flex w-full items-center justify-between rounded-[1.5rem] border border-[#D9D9D9] p-4 px-8">
                <span className="text-[2rem] font-medium text-black">
                  Max Bid
                </span>
                <Controller
                  control={control}
                  name={`maxPrice`}
                  render={({ field: { value, onChange, onBlur } }) => (
                    <FormCurrencyInputFancy
                      placeholder={'0.00'}
                      error={!!errors.maxPrice}
                      onChange={(e) => {
                        clearErrors('maxPrice');
                        onChange(e);
                      }}
                      value={value && value > 0 ? value.toFixed(2) : ''}
                      onBlur={onBlur}
                      className={`!text-[1.8rem] ${
                        errors.price ? 'text-brand-secondary' : '!text-black'
                      }`}
                    />
                  )}
                />
              </div>
              <div className="mt-4 text-[1.5rem] font-medium text-brand-dark-gray">
                We will automatically rebid up to your max bid plus $1 if you
                are out bid.
              </div>
              {errors.maxPrice && (
                <span className="text-[1.5rem] font-semibold text-brand-red ">
                  {errors.maxPrice.message}
                </span>
              )}
            </div>
          </div>

          <div className="flex w-full flex-col gap-4">
            <Checkbox
              label="I acknowledge that this bid is binding."
              onChange={() => {
                setValue('termsAndConditions', !form.termsAndConditions);
                clearErrors('termsAndConditions');
              }}
              selected={form.termsAndConditions}
              className="py-6 pl-4 !text-[1.6rem]"
              error={!!errors.termsAndConditions}
            />
            {errors.termsAndConditions && (
              <span className="text-[1.5rem] font-semibold text-brand-red">
                {errors.termsAndConditions.message}
              </span>
            )}
            <Button
              width="fluid"
              type="secondary"
              text="Continue"
              onClick={() => {
                if (checkErrors()) return;
                setCurrentStep((prev) => prev + 1);
              }}
            />
          </div>
        </div>
      ),
    },
    {
      id: 'shipping-payment',
      content: (
        <div className="flex h-full flex-col justify-between p-8">
          <div>
            <span className="text-[2.1rem] font-semibold text-black">
              Shipping & Payment
            </span>

            <div className="mt-16 flex flex-col gap-y-4">
              <div className="flex w-full justify-between">
                <span className="text-[1.8rem] font-semibold text-black">
                  Shipping To
                </span>

                <button
                  type="button"
                  onClick={() => setOpenModal('address')}
                  className="pr-4 text-[1.5rem] font-medium text-brand-secondary"
                >
                  Add
                </button>
              </div>

              {errors.shippingAddress && (
                <span className="text-[1.5rem] font-semibold text-brand-red">
                  {errors.shippingAddress.message}
                </span>
              )}

              <FormSelect
                disabled={!addressOptions.length}
                allowEmpty={false}
                placeholder="Select shipping address"
                error={!!errors.shippingAddress}
                options={addressOptions}
                rules={{ required: true }}
                control={control}
                name="shippingAddress"
                onChange={(e) => {
                  clearErrors('shippingAddress');
                  setValue('shippingAddress', e.value);

                  const selectedAddress = JSON.parse(e.value);

                  const shipping = calculateShipping(
                    product,
                    getShippingRegion(selectedAddress)
                  );
                  if (!shipping) {
                    setError('shippingAddress', {
                      message: 'No shipping options found',
                    });
                  } else {
                    setValue('shippingCost', shipping?.cost ?? 0);
                  }
                }}
              />
            </div>

            <div className="mt-10 flex flex-col gap-y-4">
              <div className="flex w-full justify-between">
                <span className="text-[1.8rem] font-semibold text-black">
                  Payment Method
                </span>

                <button
                  type="button"
                  onClick={() => setOpenModal('payment')}
                  className="pr-4 text-[1.5rem] font-medium text-brand-secondary"
                >
                  Add
                </button>
              </div>

              {!!errors.paymentMethodId && (
                <span className="text-[1.5rem] font-semibold text-brand-red">
                  {errors.paymentMethodId.message}
                </span>
              )}

              <FormSelect
                disabled={!paymentOptions?.length}
                allowEmpty={false}
                placeholder="Select payment method"
                error={!!errors.paymentMethodId}
                options={paymentOptions ?? []}
                onChange={(e) => {
                  clearErrors('paymentMethodId');
                }}
                control={control}
                defaultValue={form.paymentMethodId}
                name="paymentMethodId"
              />
            </div>
          </div>
          <Button
            text="Continue"
            type="secondary"
            width="fluid"
            onClick={() => {
              if (checkErrors()) return;
              taxMutation.mutate({
                price: form.price,
                shippingCost: form.shippingCost,
                shippingAddress: form.shippingAddress,
              });
              setCurrentStep((prev) => prev + 1);
            }}
          />
        </div>
      ),
    },
    {
      id: 'review-bid',
      content: (
        <div className="flex h-full flex-col justify-between p-8">
          <div>
            <div className="px-8">
              <span className="text-[2.1rem] font-semibold text-black">
                Review Your Bid
              </span>

              <div className="mt-8 flex items-center justify-between">
                <div className="flex max-w-[30rem] items-center gap-[1.6rem]">
                  <SafeImage
                    className="h-[6rem] w-[6rem] shrink-0 rounded-2xl"
                    alt={'product image'}
                    src={product.thumbnail}
                  />

                  <div className="flex flex-col gap-[0.2rem]">
                    <div className="flex flex-col gap-y-2">
                      <span className="w-[22rem] truncate text-[1.7rem] font-semibold text-black">
                        {product.title}
                      </span>

                      <span className="text-[1.6rem] font-medium text-[#7F7F7F]">
                        {product.category}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex grow gap-4 whitespace-nowrap">
                  <button
                    type="button"
                    className="text-[1.8rem] font-medium text-brand-secondary"
                    onClick={() => {
                      const index = steps.findIndex(
                        (step) => step.id === 'bid-price'
                      );
                      setCurrentStep(index);
                    }}
                  >
                    <PencilIcon className="h-8 w-8 text-brand-secondary" />
                  </button>
                  <div className="flex flex-col items-end gap-y-2">
                    <span className="text-[1.8rem] font-bold text-black">
                      Bid: {formatCurrency(form.price)}
                    </span>
                    {form.maxPrice && (
                      <span className="text-[1.5rem] font-semibold text-brand-dark-gray">
                        Max: {formatCurrency(form.maxPrice)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 h-px w-full bg-[#F1F1F1]" />

            <div className="px-8">
              <div className="mt-16 flex flex-col gap-y-4">
                <div className="flex w-full justify-between">
                  <span className="text-[1.8rem] font-semibold text-black">
                    Shipping To
                  </span>

                  <button
                    onClick={() => {
                      const index = steps.findIndex(
                        (step) => step.id === 'shipping-payment'
                      );
                      setCurrentStep(index);
                    }}
                    className="pr-4 text-[1.5rem] font-medium text-brand-secondary"
                    type="button"
                  >
                    Edit
                  </button>
                </div>

                {form.shippingAddress ? (
                  <div className="flex items-center gap-x-4">
                    <div className="flex items-center gap-x-2">
                      <span className="text-[1.8rem] font-medium text-black">
                        {JSON.parse(form.shippingAddress).address_line1},{' '}
                        {JSON.parse(form.shippingAddress).city_locality},{' '}
                        {JSON.parse(form.shippingAddress).state_province}{' '}
                        {JSON.parse(form.shippingAddress).postal_code}
                      </span>
                    </div>
                  </div>
                ) : (
                  <span className="text-[1.5rem] font-semibold text-brand-red">
                    Please select a shipping address
                  </span>
                )}

                {errors.shippingAddress && (
                  <span className="text-[1.5rem] font-semibold text-brand-red">
                    {errors.shippingAddress.message}
                  </span>
                )}
              </div>

              <div className="mt-10 flex flex-col gap-y-4">
                <div className="flex w-full justify-between">
                  <span className="text-[1.8rem] font-semibold text-black">
                    Payment Method
                  </span>

                  <button
                    onClick={() => {
                      const index = steps.findIndex(
                        (step) => step.id === 'shipping-payment'
                      );
                      setCurrentStep(index);
                    }}
                    className="pr-4 text-[1.5rem] font-medium text-brand-secondary"
                    type="button"
                  >
                    Edit
                  </button>
                </div>
                {form.paymentMethodId &&
                paymentMethodData?.find(
                  (pm) => pm.id === form.paymentMethodId
                ) ? (
                  <div className="flex items-center gap-x-4">
                    {['visa', 'mastercard', 'discover', 'amex'].includes(
                      paymentMethodData
                        .find((pm) => pm.id === form.paymentMethodId)
                        ?.brand?.toLowerCase() ?? ''
                    ) && (
                      <SafeImage
                        src={`/cc_brands/${paymentMethodData
                          .find((pm) => pm.id === form.paymentMethodId)
                          ?.brand?.toLowerCase()}.png`}
                        alt={
                          paymentMethodData.find(
                            (pm) => pm.id === form.paymentMethodId
                          )?.brand ?? ''
                        }
                        width={32}
                        height={20}
                        className="mr-2 h-20 w-20 object-contain"
                      />
                    )}

                    <span className="text-[1.8rem] font-medium text-black">
                      {`Card ending in ${
                        paymentMethodData.find(
                          (pm) => pm.id === form.paymentMethodId
                        )?.last4
                      }`}
                    </span>
                  </div>
                ) : (
                  <span className="text-[1.5rem] font-semibold text-brand-red">
                    Please select a payment method
                  </span>
                )}
                {errors.paymentMethodId && (
                  <span className="text-[1.5rem] font-semibold text-brand-red">
                    {errors.paymentMethodId.message}
                  </span>
                )}
              </div>
            </div>

            <div className="mt-8 h-px w-full bg-[#F1F1F1]" />

            <div className="mt-8 px-8">
              <div className="flex flex-col gap-y-6">
                <div className="flex items-center justify-between">
                  <span className="text-[1.8rem] font-medium text-[#444444]">
                    Your Bid
                  </span>

                  <span className="text-[1.8rem] font-medium text-[#444444]">
                    {formatCurrency(form.price)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-[1.8rem] font-medium text-[#444444]">
                    Shipping
                  </span>

                  <span className="text-[1.8rem] font-medium text-[#444444]">
                    {formatCurrency(form.shippingCost)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-[1.8rem] font-medium text-[#444444]">
                    Tax
                  </span>

                  {taxMutation.isPending ? (
                    <span className="h-10 w-20 animate-pulse rounded-full bg-gray-200 text-[1.8rem] font-medium"></span>
                  ) : taxMutation.error ? (
                    <span className="text-[1.8rem] font-medium text-brand-red">
                      {taxMutation.error.message}
                    </span>
                  ) : (
                    <span className="text-[1.8rem] font-medium text-[#444444]">
                      {formatCurrency(form.tax)}
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-[1.8rem] font-bold text-black">
                    You will pay
                  </span>

                  {taxMutation.isPending ? (
                    <span className="h-10 w-20 animate-pulse rounded-full bg-gray-200 text-[1.8rem] font-medium"></span>
                  ) : taxMutation.error ? (
                    <span className="text-[1.8rem] font-medium text-brand-red">
                      {taxMutation.error.message}
                    </span>
                  ) : (
                    <span className="text-[2rem] font-bold text-black">
                      {formatCurrency(
                        form.price + form.shippingCost + form.tax
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div>
            {errors.root && (
              <span className="text-[1.5rem] font-semibold text-brand-red">
                {errors.root.message}
              </span>
            )}
            <Button
              text="Place Bid"
              type="secondary"
              width="fluid"
              buttonType="submit"
              loading={isSubmitting}
            />
          </div>
        </div>
      ),
    },
  ];
  const checkErrors = () => {
    let hasErrors = false;
    switch (steps[currentStep].id) {
      case 'bid-price':
        if (!form.termsAndConditions) {
          hasErrors = true;
          setError('termsAndConditions', {
            message: 'Please acknowledge that this bid is binding.',
          });
        }
        if (!form.price) {
          hasErrors = true;
          setError('price', {
            message: 'Please enter a valid price',
          });
        }
        if (highestBid && form.price < highestBid.bid_price + 1) {
          hasErrors = true;
          setError('price', {
            message: `Your bid must be at least ${formatCurrency(
              1
            )} higher than the current bid: ${formatCurrency(
              highestBid.bid_price
            )}.`,
          });
        } else if (form.price < product.price + 1) {
          hasErrors = true;
          setError('price', {
            message: `Your bid must be at least ${formatCurrency(
              1
            )} higher than the starting bid: ${formatCurrency(product.price)}.`,
          });
        }
        if (form.maxPrice && form.maxPrice < form.price + 1) {
          hasErrors = true;
          setError('maxPrice', {
            message: `Your max bid must be at least ${formatCurrency(
              1
            )} higher than your current bid.`,
          });
        }
        break;
      case 'shipping-payment':
        if (!form.shippingAddress) {
          hasErrors = true;
          setError('shippingAddress', {
            message: 'Please select a shipping address',
          });
        }
        if (!form.paymentMethodId) {
          hasErrors = true;
          setError('paymentMethodId', {
            message: 'Please select a payment method',
          });
        }
        break;
      case 'review-bid':
        if (taxMutation.isPending) {
          hasErrors = true;
          setError('root', {
            message: 'Calculating tax. Please wait...',
          });
        } else if (taxMutation.error) {
          hasErrors = true;
          setError('root', {
            message: taxMutation.error.message,
          });
        }
    }
    return hasErrors;
  };

  return (
    <div className="w-full overflow-x-hidden sm:w-[55rem]">
      <div className="h-px w-full bg-[#F1F1F1]" />
      <div className="w-full px-8">
        <div className="flex w-full justify-between gap-x-4 pb-8">
          {steps.map((step, index) => {
            const isActive = index === currentStep;
            return (
              <div
                key={step.id}
                className={`h-1 w-full rounded-full ${
                  isActive ? 'bg-brand-secondary' : 'bg-zinc-200'
                }`}
              />
            );
          })}
        </div>
      </div>
      <form className="h-[70rem] w-full" onSubmit={handleSubmit(onSubmit)}>
        <BProcess steps={steps} currentStep={currentStep} />
      </form>
      {openModal === 'address' && (
        <AddAddressModal
          isOpen={true}
          dismiss={(address) => {
            setOpenModal('');
            if (address) {
              setValue('shippingAddress', JSON.stringify(address));
            }
          }}
        />
      )}
      {openModal === 'payment' && (
        <AddPaymentMethodModal
          isOpen={true}
          dismiss={(paymentMethod) => {
            setOpenModal('');
            if (paymentMethod) {
              setValue('paymentMethodId', paymentMethod);
            }
          }}
        />
      )}
    </div>
  );
};
